class BBCountDown extends HTMLElement {
  static get observedAttributes() {
    return ['data-expiration'];
  }

  constructor() {
    super();
    this.intervalId = null;
    this.expirationDate = null;
    this.hasNotifiedExpiry = false;
    this.unitElements = null;
  }

  connectedCallback() {
    this.setAttribute('role', this.getAttribute('role') || 'timer');
    this.setAttribute('aria-live', this.getAttribute('aria-live') || 'polite');
    this.cacheUnitElements();
    this.refreshExpiration();
    this.startTicker();
  }

  disconnectedCallback() {
    this.stopTicker();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-expiration' && oldValue !== newValue) {
      this.refreshExpiration();
    }
  }

  cacheUnitElements() {
    this.unitElements = {
      days: this.querySelector('[data-unit="days"]'),
      hours: this.querySelector('[data-unit="hours"]'),
      minutes: this.querySelector('[data-unit="minutes"]'),
      seconds: this.querySelector('[data-unit="seconds"]'),
    };
  }

  refreshExpiration() {
    this.stopTicker();
    this.hasNotifiedExpiry = false;
    this.classList.remove('is-expired');
    this.expirationDate = this.parseExpiration(this.dataset.expiration);
    this.updateDisplay();
    this.startTicker();
  }

  parseExpiration(rawValue) {
    if (!rawValue || typeof rawValue !== 'string') {
      return null;
    }

    var trimmed = rawValue.trim();
    if (!trimmed) {
      return null;
    }

    var normalized = trimmed.replace(' ', 'T');
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      normalized = trimmed + 'T00:00';
    }
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(normalized)) {
      normalized += ':00';
    }

    var candidate = new Date(normalized);
    return isNaN(candidate.getTime()) ? null : candidate;
  }

  startTicker() {
    if (!this.expirationDate) {
      return;
    }
    if (this.intervalId !== null) {
      return;
    }

    this.intervalId = window.setInterval(
      function () {
        this.updateDisplay();
      }.bind(this),
      1000,
    );
  }

  stopTicker() {
    if (this.intervalId !== null) {
      window.clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  updateDisplay() {
    var units = this.calculateRemaining();
    this.renderUnits(units);

    if (units.totalMilliseconds <= 0 && !this.hasNotifiedExpiry) {
      this.hasNotifiedExpiry = true;
      this.classList.add('is-expired');
      this.stopTicker();
    } else if (units.totalMilliseconds > 0) {
      this.classList.remove('is-expired');
    }
  }

  calculateRemaining() {
    if (!this.expirationDate) {
      return {
        days: '--',
        hours: '--',
        minutes: '--',
        seconds: '--',
        totalMilliseconds: -1,
      };
    }

    var now = new Date();
    var totalMilliseconds = this.expirationDate.getTime() - now.getTime();
    var remaining = Math.max(totalMilliseconds, 0);

    var totalSeconds = Math.floor(remaining / 1000);
    var days = Math.floor(totalSeconds / 86400);
    var hours = Math.floor((totalSeconds % 86400) / 3600);
    var minutes = Math.floor((totalSeconds % 3600) / 60);
    var seconds = totalSeconds % 60;

    return {
      days: days,
      hours: hours,
      minutes: minutes,
      seconds: seconds,
      totalMilliseconds: totalMilliseconds,
    };
  }

  renderUnits(units) {
    this.cacheUnitElements();

    this.updateUnit(this.unitElements.days, units.days, false);
    this.updateUnit(this.unitElements.hours, units.hours, true);
    this.updateUnit(this.unitElements.minutes, units.minutes, true);
    this.updateUnit(this.unitElements.seconds, units.seconds, true);
  }

  updateUnit(target, value, pad) {
    if (!target) {
      return;
    }
    var output = value;
    if (typeof value === 'number' && pad) {
      output = this.padNumber(value);
    }
    if (typeof value === 'number' && !pad) {
      output = String(value);
    }
    if (typeof value === 'string') {
      output = value;
    }
    if (target.textContent !== output) {
      target.textContent = output;
    }
  }

  padNumber(value) {
    return value < 10 ? '0' + value : String(value);
  }
}

if (!customElements.get('bb-countdown')) {
  customElements.define('bb-countdown', BBCountDown);
}
